import { startTransition, useCallback, useEffect, useState } from "react";
import type { TabId } from "@/components/ui-shell/TabNavigation";
import {
  DEFAULT_RUN_HISTORY_QUERY,
  type RunHistoryKind,
  type RunHistoryQuery,
  type RunHistoryStatus,
} from "./status-domain";

type HistoryMode = "push" | "replace";

type AppViewState = {
  activeTab: TabId;
  runHistoryQuery: RunHistoryQuery;
};

type UpdateRunHistoryQuery = (
  updater: (prev: RunHistoryQuery) => RunHistoryQuery,
  historyMode?: HistoryMode,
) => void;

type UseStatusViewStateResult = {
  activeTab: TabId;
  runHistoryQuery: RunHistoryQuery;
  setActiveTab: (nextTab: TabId, historyMode?: HistoryMode) => void;
  setRunHistoryQuery: UpdateRunHistoryQuery;
  resetViewState: () => void;
};

const VALID_TABS = new Set<TabId>(["upload", "status", "verify"]);
const VALID_KINDS = new Set<RunHistoryKind>([
  "all",
  "fatture_ddt",
  "polizza_carico",
]);
const VALID_STATUSES = new Set<RunHistoryStatus>([
  "all",
  "queued",
  "processing",
  "completed",
  "failed",
]);

function parsePage(value: string | null): number {
  if (!value) return DEFAULT_RUN_HISTORY_QUERY.page;
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed) || parsed < 1) {
    return DEFAULT_RUN_HISTORY_QUERY.page;
  }
  return parsed;
}

function readViewState(): AppViewState {
  const params = new URLSearchParams(window.location.search);
  const tabParam = params.get("tab");
  const kindParam = params.get("runKind");
  const statusParam = params.get("runStatus");

  return {
    activeTab: VALID_TABS.has(tabParam as TabId)
      ? (tabParam as TabId)
      : "upload",
    runHistoryQuery: {
      kind: VALID_KINDS.has(kindParam as RunHistoryKind)
        ? (kindParam as RunHistoryKind)
        : DEFAULT_RUN_HISTORY_QUERY.kind,
      status: VALID_STATUSES.has(statusParam as RunHistoryStatus)
        ? (statusParam as RunHistoryStatus)
        : DEFAULT_RUN_HISTORY_QUERY.status,
      fileQuery: params.get("runFile") ?? DEFAULT_RUN_HISTORY_QUERY.fileQuery,
      page: parsePage(params.get("runPage")),
    },
  };
}

function buildSearch(state: AppViewState): string {
  const params = new URLSearchParams();

  if (state.activeTab !== "upload") {
    params.set("tab", state.activeTab);
  }

  if (state.runHistoryQuery.kind !== DEFAULT_RUN_HISTORY_QUERY.kind) {
    params.set("runKind", state.runHistoryQuery.kind);
  }
  if (state.runHistoryQuery.status !== DEFAULT_RUN_HISTORY_QUERY.status) {
    params.set("runStatus", state.runHistoryQuery.status);
  }
  if (state.runHistoryQuery.fileQuery.trim()) {
    params.set("runFile", state.runHistoryQuery.fileQuery.trim());
  }
  if (state.runHistoryQuery.page !== DEFAULT_RUN_HISTORY_QUERY.page) {
    params.set("runPage", String(state.runHistoryQuery.page));
  }

  const encoded = params.toString();
  return encoded ? `?${encoded}` : "";
}

function areRunHistoryQueriesEqual(
  left: RunHistoryQuery,
  right: RunHistoryQuery,
): boolean {
  return (
    left.kind === right.kind &&
    left.status === right.status &&
    left.fileQuery === right.fileQuery &&
    left.page === right.page
  );
}

function syncBrowserUrl(state: AppViewState, historyMode: HistoryMode) {
  const nextSearch = buildSearch(state);
  const nextUrl = `${window.location.pathname}${nextSearch}${window.location.hash}`;

  if (historyMode === "push") {
    window.history.pushState(null, "", nextUrl);
    return;
  }

  window.history.replaceState(null, "", nextUrl);
}

export function useStatusViewState(): UseStatusViewStateResult {
  const [viewState, setViewState] = useState<AppViewState>(() =>
    readViewState(),
  );

  useEffect(() => {
    const handlePopState = () => {
      setViewState(readViewState());
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const setActiveTab = useCallback(
    (nextTab: TabId, historyMode: HistoryMode = "push") => {
      startTransition(() => {
        setViewState((prev) => {
          if (prev.activeTab === nextTab) {
            return prev;
          }

          const nextState = {
            ...prev,
            activeTab: nextTab,
          };
          syncBrowserUrl(nextState, historyMode);
          return nextState;
        });
      });
    },
    [],
  );

  const setRunHistoryQuery = useCallback<UpdateRunHistoryQuery>(
    (updater, historyMode = "replace") => {
      startTransition(() => {
        setViewState((prev) => {
          const nextQuery = updater(prev.runHistoryQuery);
          const normalizedNextQuery = {
            ...nextQuery,
            page: Math.max(1, nextQuery.page),
          };

          if (
            areRunHistoryQueriesEqual(prev.runHistoryQuery, normalizedNextQuery)
          ) {
            return prev;
          }

          const nextState = {
            ...prev,
            runHistoryQuery: normalizedNextQuery,
          };
          syncBrowserUrl(nextState, historyMode);
          return nextState;
        });
      });
    },
    [],
  );

  const resetViewState = useCallback(() => {
    startTransition(() => {
      const nextState = {
        activeTab: "upload" as const,
        runHistoryQuery: DEFAULT_RUN_HISTORY_QUERY,
      };
      syncBrowserUrl(nextState, "replace");
      setViewState(nextState);
    });
  }, []);

  return {
    activeTab: viewState.activeTab,
    runHistoryQuery: viewState.runHistoryQuery,
    setActiveTab,
    setRunHistoryQuery,
    resetViewState,
  };
}
